import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const Swal = window.Swal;

const EditProject = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const formRef = useRef(null);

    const [, setBaseUrl] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        location: "",
        isFeatured: false,
        isCompleted: false,

        hero: {
            description: "",
            buttonLink: "",
        },

        imgBox: {
            title: "",
            description: "",
            buttonText: "",
            buttonLink: "",
            officeSpace: "",
            retailSpace: "",
            area: "",
        },
    });

    const [imageSrc, setImageSrc] = useState(null);
    const [imageSrcPreview, setImageSrcPreview] = useState(null);

    const [featuredImg, setFeaturedImg] = useState(null);
    const [featuredImgPreview, setFeaturedImgPreview] = useState(null);

    const [heroImage, setHeroImage] = useState(null);
    const [heroImagePreview, setHeroImagePreview] = useState(null);

    const [imgBoxImage, setImgBoxImage] = useState(null);
    const [imgBoxImagePreview, setImgBoxImagePreview] = useState(null);

    const [heroButtonFile, setHeroButtonFile] = useState(null);
    const [heroButtonUrl, setHeroButtonUrl] = useState(null);

    const [imgBoxButtonFile, setImgBoxButtonFile] = useState(null);
    const [imgBoxButtonUrl, setImgBoxButtonUrl] = useState(null);

    const [gallery, setGallery] = useState([
        {
            image: null,
            preview: null,
            type: "interior",
            include_dynamic_page: false,
        },
    ]);

    const [mobileGallery, setMobileGallery] = useState([
        {
            image: null,
            preview: null,
            type: "interior",
            include_dynamic_page: false,
        },
    ]);

    const [projectStatistics, setProjectStatistics] = useState([
        { key: "", value: "" }
    ]);

    useEffect(() => {

        const fetchProject = async () => {
            try {
                const res = await api.get(`/projects/${slug}`);
                const p = res.data.project;
                const baseUrl = res.data.base_url;

                setBaseUrl(baseUrl);
                setFormData({
                    title: p.title || "",
                    description: p.description || "",
                    location: p.location || "",
                    isFeatured: p.isFeatured || false,
                    isCompleted: p.isCompleted || false,
                    hero: {
                        description: p.hero?.description || "",
                        buttonLink: "",
                    },
                    imgBox: {
                        title: p.imgBox?.title || "",
                        description: p.imgBox?.description || "",
                        buttonText: p.imgBox?.buttonText || "",
                        officeSpace: p.imgBox?.officeSpace || "",
                        retailSpace: p.imgBox?.retailSpace || "",
                        area: p.imgBox?.area || "",
                        buttonLink: "",
                    },
                });

                setFeaturedImgPreview(p.featuredImg ? baseUrl + p.featuredImg : null);
                setImageSrcPreview(p.imageSrc ? baseUrl + p.imageSrc : null);
                setHeroImagePreview(p.hero?.image ? baseUrl + p.hero?.image : null);
                setImgBoxImagePreview(p.imgBox?.imgSrc ? baseUrl + p.imgBox?.imgSrc : null);

                setHeroButtonUrl(p.hero?.buttonLink ? baseUrl + p.hero?.buttonLink : null);
                setImgBoxButtonUrl(p.imgBox?.buttonLink ? baseUrl + p.imgBox?.buttonLink : null);

                setGallery(
                    p.projectGallery.map(g => ({
                        image: null,
                        preview: baseUrl + g.image,
                        type: g.type,
                        include_dynamic_page: g.include_dynamic_page,
                        existingImage: g.image
                    }))
                );

                if (Array.isArray(p.mobileGallery) && p.mobileGallery.length) {
                    setMobileGallery(
                        p.mobileGallery.map(g => ({
                            image: null,
                            preview: baseUrl + g.image,
                            type: g.type,
                            include_dynamic_page: g.include_dynamic_page,
                            existingImage: g.image
                        }))
                    );
                }

                setProjectStatistics(
                    Array.isArray(p.projectStatistics) && p.projectStatistics.length
                        ? p.projectStatistics.map(s => ({
                            key: s.key || "",
                            value: s.value || ""
                        }))
                        : [{ key: "", value: "" }]
                );

            } catch (err) {
                handleErrors(err);
            }
        };

        fetchProject();

        if (!$.validator.methods.extension) {
            $.validator.addMethod('extension', function (value, element, allowedExtensions) {
                if (!value) return true;
                const ext = value.split('.').pop().toLowerCase();
                return allowedExtensions.includes(ext);
            });
        }

        if (formRef.current && window.$.fn.validate) {
            window.$(formRef.current).validate({
                ignore: ":hidden:not(.validate),.ck-editor *",
                rules: {
                    title: { required: true, minlength: 2 },
                    location: { required: true },
                    description: { required: true, minlength: 3 },
                    hero_description: { required: true, minlength: 3 },
                    hero_buttonLink: {
                        required: false,
                        extension: ['pdf', 'docx']
                    },
                    imgBox_title: { required: true },
                    imgBox_description: { required: true, minlength: 3 },
                    imgBox_buttonText: { required: true },
                    imgBox_buttonLink: {
                        required: false,
                        extension: ['pdf', 'docx']
                    },
                    imgBox_officeSpace: { required: true },
                    imgBox_retailSpace: { required: true },
                    imgBox_area: { required: true },
                    imageSrc: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'pdf', 'svg', 'webp']
                    },
                    featuredImg: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'pdf', 'svg', 'webp']
                    },
                    hero_image: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'pdf', 'svg', 'webp']
                    },
                    imgBox_imgSrc: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'pdf', 'svg', 'webp']
                    },
                },
                messages: {
                    title: {
                        required: "Project title is required",
                        minlength: "Title must be at least 2 characters long"
                    },
                    location: {
                        required: "Location is required"
                    },
                    description: {
                        required: "Description is required",
                        minlength: "Description must be at least 3 characters"
                    },
                    hero_description: {
                        required: "Hero description is required",
                        minlength: "Hero description must be at least 10 characters"
                    },
                    imgBox_title: {
                        required: "Image box title is required"
                    },
                    imgBox_description: {
                        required: "Image box description is required",
                        minlength: "Image box description must be at least 10 characters"
                    },
                    imgBox_buttonText: {
                        required: "Image box button text is required"
                    },
                    hero_buttonLink: {
                        required: "Hero button file is required",
                        extension: "Only PDF or DOCX files are allowed"
                    },
                    imgBox_buttonLink: {
                        required: "ImgBox button file is required",
                        extension: "Only PDF or DOCX files are allowed"
                    },
                    imgBox_officeSpace: {
                        required: "Office space is required"
                    },
                    imgBox_retailSpace: {
                        required: "Retail space is required"
                    },
                    imgBox_area: {
                        required: "Area is required"
                    },
                    featuredImg: {
                        required: "Featured image is required",
                        extension: "Allowed formats: jpg, jpeg, png, pdf, svg"
                    },
                    imageSrc: {
                        required: "Main project image is required",
                        extension: "Allowed formats: jpg, jpeg, png, pdf, svg"
                    },
                    hero_image: {
                        required: "Hero image is required",
                        extension: "Allowed formats: jpg, jpeg, png, pdf, svg"
                    },
                    imgBox_imgSrc: {
                        required: "Boxes image is required",
                        extension: "Allowed formats: jpg, jpeg, png, pdf, svg"
                    }
                },
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }

    }, [slug]);

    useEffect(() => {
        return () => {
            featuredImgPreview && URL.revokeObjectURL(featuredImgPreview);
            imageSrcPreview && URL.revokeObjectURL(imageSrcPreview);
            heroImagePreview && URL.revokeObjectURL(heroImagePreview);
            imgBoxImagePreview && URL.revokeObjectURL(imgBoxImagePreview);
        };
    }, [featuredImgPreview, imageSrcPreview, heroImagePreview, imgBoxImagePreview]);

    /*
    const toggleCheckbox = (e) => {
        let { name, value } = e.target;        
        value === 'false'? value = true: value = false;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    */

    const toggleCheckbox = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
    };

    const handleRemoveGalleryImage = async (index) => {
        const item = gallery[index];

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This image will be permanently removed.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, remove it',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        if (item.existingImage) {
            try {
                await api.delete(`/projects/${slug}/gallery-image`, {
                    data: {
                        image: item.existingImage
                    }
                });

                showToast('success', 'Gallery image removed');
            } catch (err) {
                handleErrors(err);
                return;
            }
        }

        setGallery(prev => prev.filter((_, i) => i !== index));
    };

    const handleRemoveHeroButtonFile = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'The Hero Button File will be permanently removed.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, remove it',
        });

        if (!result.isConfirmed) return;

        try {
            const res = await api.delete(`/projects/${slug}/hero-button-file`);
            if (res.status === 200) {
                showToast('success', res.data.message);
                setHeroButtonUrl(null);
            }
        } catch (err) {
            handleErrors(err);
        }
    };

    const handleRemoveImgBoxButtonFile = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'The Image Box Button File will be permanently removed.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, remove it',
        });

        if (!result.isConfirmed) return;

        try {
            const res = await api.delete(`/projects/${slug}/imgbox-button-file`);
            if (res.status === 200) {
                showToast('success', res.data.message);
                setImgBoxButtonUrl(null);
            }
        } catch (err) {
            handleErrors(err);
        }
    };

    const handleSingleImageChange = (e, setFile, setPreview) => {
        const file = e.target.files[0];
        if (file) {
            setFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleGalleryImageChange = (index, file) => {
        const updated = [...gallery];
        updated[index].image = file;
        updated[index].preview = URL.createObjectURL(file);
        setGallery(updated);
    };

    const handleMobileGalleryImageChange = (index, file) => {
        const updated = [...mobileGallery];
        updated[index].image = file;
        updated[index].preview = URL.createObjectURL(file);
        setMobileGallery(updated);
    };

    const handleRemoveMobileGalleryImage = async (index) => {
        const item = mobileGallery[index];

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This mobile gallery image will be permanently removed.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, remove it',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        if (item.existingImage) {
            try {
                await api.delete(`/projects/${slug}/mobile-gallery-image`, {
                    data: {
                        image: item.existingImage
                    }
                });

                showToast('success', 'Mobile gallery image removed');
            } catch (err) {
                handleErrors(err);
                return;
            }
        }

        setMobileGallery(prev => prev.filter((_, i) => i !== index));
    };

    const handleStatisticChange = (index, field, value) => {
        const updated = [...projectStatistics];
        updated[index][field] = value;
        setProjectStatistics(updated);
    };

    const addStatistic = () => {
        setProjectStatistics(prev => [...prev, { key: "", value: "" }]);
    };

    const removeStatistic = (index) => {
        setProjectStatistics(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (!form.valid()) return;

        const data = new FormData();

        data.append("title", formData.title);
        data.append("description", formData.description);
        data.append("location", formData.location);
        data.append("isFeatured", String(formData.isFeatured));
        data.append("isCompleted", String(formData.isCompleted));

        delete formData.hero.buttonLink
        delete formData.imgBox.buttonLink

        data.append("hero", JSON.stringify(formData.hero));
        data.append("imgBox", JSON.stringify(formData.imgBox));

        if (featuredImg) data.append("featuredImg", featuredImg);
        if (imageSrc) data.append("imageSrc", imageSrc);
        if (heroImage) data.append("heroImage", heroImage);
        if (imgBoxImage) data.append("imgBoxImage", imgBoxImage);

        if (heroButtonFile instanceof File) {
            data.append("heroButtonLink", heroButtonFile);
        }
        if (imgBoxButtonFile instanceof File) {
            data.append("imgBoxButtonLink", imgBoxButtonFile);
        }

        const galleryMeta = [];

        gallery.forEach(item => {
            if (item.image instanceof File) {
                data.append("projectGalleryImages", item.image);
            }
            galleryMeta.push({
                type: item.type,
                include_dynamic_page: item.include_dynamic_page
            });
        });

        if (galleryMeta.length) {
            data.append("projectGallery", JSON.stringify(galleryMeta));
        }

        const mobileGalleryMeta = [];

        mobileGallery.forEach(item => {
            if (item.image instanceof File) {
                data.append("mobileGalleryImages", item.image);
            }
            mobileGalleryMeta.push({
                type: item.type,
                include_dynamic_page: item.include_dynamic_page
            });
        });

        if (mobileGalleryMeta.length) {
            data.append("mobileGallery", JSON.stringify(mobileGalleryMeta));
        }

        const validStats = projectStatistics.filter(
            s => s.key.trim() && s.value.trim()
        );

        if (validStats.length === 0) {
            showToast("error", "At least one project statistic is required");
            return;
        }

        data.append("projectStatistics", JSON.stringify(validStats));

        try {
            setSubmitting(true);
            const res = await api.put(`/projects/${slug}`, data, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            showToast("success", res.data.message || "Project updated");
            navigate(ROUTES.PROJECTS);
        } catch (err) {
            handleErrors(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.PROJECTS}>Projects</Link>
                    </li>
                    <li className="breadcrumb-item active">Edit Project</li>
                </ol>
            </div>

            <form ref={formRef} noValidate>
                <div className="card">
                    <div className="card-header">
                        <h3>BASIC</h3>
                    </div>
                    <div className="card-body">
                        <div className="row">

                            {/* BASIC */}
                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="title" className="form-label">Title</label>
                                <input
                                    className="form-control mb-2"
                                    id="title"
                                    name="title"
                                    placeholder="Title"
                                    value={formData.title}
                                    onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="location" className="form-label">Location</label>
                                <input
                                    className="form-control mb-2"
                                    id="location"
                                    name="location"
                                    placeholder="Location"
                                    value={formData.location}
                                    onChange={e => setFormData(p => ({ ...p, location: e.target.value }))}
                                />
                            </div>

                            {[
                                { name: 'description', label: 'Description' }
                            ].map(({ name, label }) => (
                                <div key={name} className="mb-3 col-lg-12 col-md-12 col-sm-12">
                                    <label className="form-label">{label}</label>

                                    <CKEditor
                                        editor={ClassicEditor}
                                        config={{
                                            toolbar: [
                                                'heading', '|',
                                                'bold', 'italic', 'link', 'code', 'codeBlock',
                                                '|', 'bulletedList', 'numberedList'
                                            ]
                                        }}
                                        data={formData.description}
                                        onChange={(event, editor) => {
                                            const html = editor.getData();
                                            const plainText = html.replace(/<[^>]*>/g, '').trim();

                                            setFormData(p => ({
                                                ...p,
                                                description: html
                                            }));

                                            const input = document.querySelector('input[name="description"]');
                                            input.value = plainText;

                                            $(input).valid();
                                        }}
                                    />

                                    <input
                                        type="hidden"
                                        name="description"
                                        className="validate"
                                        value={formData.description}
                                    />
                                </div>
                            ))}

                            <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                <label className="form-label">Featured Image</label>
                                <input
                                    type="file"
                                    id="featuredImg"
                                    name="featuredImg"
                                    className="form-control"
                                    onChange={(e) =>
                                        handleSingleImageChange(e, setFeaturedImg, setFeaturedImgPreview)
                                    }
                                />
                                {featuredImgPreview && (
                                    <img
                                        src={featuredImgPreview}
                                        alt="Featured Preview"
                                        style={{ width: 100, height: 100, objectFit: "cover", marginTop: 10 }}
                                    />
                                )}
                            </div>

                            <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                <label className="form-label">Main Image</label>
                                <input
                                    type="file"
                                    id="imageSrc"
                                    name="imageSrc"
                                    className="form-control"
                                    onChange={(e) =>
                                        handleSingleImageChange(e, setImageSrc, setImageSrcPreview)
                                    }
                                />
                                {imageSrcPreview && (
                                    <img
                                        src={imageSrcPreview}
                                        alt="Main Preview"
                                        style={{ width: 100, height: 100, objectFit: "cover", marginTop: 10 }}
                                    />
                                )}
                            </div>

                            <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                <div className="form-check custom-checkbox mb-3 checkbox-success">
                                    <input
                                        type="checkbox"
                                        id="isFeatured"
                                        name="isFeatured"
                                        className="form-check-input me-2"
                                        checked={formData.isFeatured}
                                        value={formData.isFeatured}
                                        onChange={toggleCheckbox}
                                    />
                                    <label className="form-check-label" htmlFor="isFeatured">Is Featured</label>
                                </div>
                            </div>

                            <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                <div className="form-check custom-checkbox mb-3 checkbox-success">
                                    <input
                                        type="checkbox"
                                        id="isCompleted"
                                        name="isCompleted"
                                        className="form-check-input me-2"
                                        checked={formData.isCompleted}
                                        value={formData.isCompleted}
                                        onChange={toggleCheckbox}
                                    />
                                    <label className="form-check-label" htmlFor="isCompleted">Is Completed</label>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h3>HERO</h3>
                    </div>
                    <div className="card-body">
                        <div className="row">

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="hero_buttonLink" className="form-label">
                                    Hero Button File (PDF/DOCX)
                                </label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="hero_buttonLink"
                                    name="hero_buttonLink"
                                    accept=".pdf,.docx"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        setHeroButtonFile(file || null);
                                    }}
                                />
                                {heroButtonUrl && !heroButtonFile && (
                                    <div className="mt-2 d-flex align-items-center gap-2">
                                        <a href={heroButtonUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                            View existing PDF
                                        </a>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={handleRemoveHeroButtonFile}
                                        >
                                            <i className="fa fa-trash"></i>
                                        </button>
                                    </div>
                                )}

                                {heroButtonFile && (
                                    <small className="text-success">Selected: {heroButtonFile.name}</small>
                                )}
                            </div>


                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label className="form-label">Image</label>
                                <input
                                    type="file"
                                    id="hero_image"
                                    name="hero_image"
                                    className="form-control"
                                    onChange={(e) =>
                                        handleSingleImageChange(e, setHeroImage, setHeroImagePreview)
                                    }
                                />
                                {heroImagePreview && (
                                    <img
                                        src={heroImagePreview}
                                        alt="Hero Preview"
                                        style={{ width: 100, height: 100, objectFit: "cover", marginTop: 10 }}
                                    />
                                )}
                            </div>

                            <div className="mb-3 col-lg-12 col-md-12 col-sm-12">
                                <label className="form-label">Description</label>
                                <CKEditor
                                    editor={ClassicEditor}
                                    config={{
                                        toolbar: [
                                            'heading', '|',
                                            'bold', 'italic', 'link', 'code', 'codeBlock',
                                            '|', 'bulletedList', 'numberedList'
                                        ]
                                    }}
                                    data={formData.hero.description}
                                    onChange={(event, editor) => {
                                        const html = editor.getData();
                                        const plainText = html.replace(/<[^>]*>/g, '').trim();

                                        setFormData(p => ({
                                            ...p,
                                            hero: { ...p.hero, description: html }
                                        }));

                                        const input = document.querySelector('input[name="hero_description"]');
                                        input.value = plainText;
                                        $(input).valid();
                                    }}
                                />
                                <input
                                    type="hidden"
                                    name="hero_description"
                                    className="validate"
                                    value={formData.hero.description}
                                />
                            </div>

                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header">
                        <h3>IMG BOX</h3>
                    </div>
                    <div className="card-body">
                        <div className="row">

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_title" className="form-label">Title</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_title"
                                    name="imgBox_title"
                                    placeholder="Title"
                                    value={formData?.imgBox?.title}
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, title: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_area" className="form-label">Area</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_area"
                                    name="imgBox_area"
                                    placeholder="Area"
                                    value={formData?.imgBox?.area}
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, area: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-12 col-md-12 col-sm-12">
                                <label className="form-label">Description</label>
                                <CKEditor
                                    editor={ClassicEditor}
                                    config={{
                                        toolbar: [
                                            'heading', '|',
                                            'bold', 'italic', 'link', 'code', 'codeBlock',
                                            '|', 'bulletedList', 'numberedList'
                                        ]
                                    }}
                                    data={formData.imgBox.description}
                                    onChange={(event, editor) => {
                                        const html = editor.getData();
                                        const plainText = html.replace(/<[^>]*>/g, '').trim();

                                        setFormData(p => ({
                                            ...p,
                                            imgBox: { ...p.imgBox, description: html }
                                        }));

                                        const input = document.querySelector('input[name="imgBox_description"]');
                                        input.value = plainText;
                                        $(input).valid();
                                    }}
                                />
                                <input
                                    type="hidden"
                                    name="imgBox_description"
                                    className="validate"
                                    value={formData.imgBox.description}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_buttonText" className="form-label">Button Text</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_buttonText"
                                    name="imgBox_buttonText"
                                    placeholder="Button Text"
                                    value={formData?.imgBox?.buttonText}
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, buttonText: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_buttonLink" className="form-label">
                                    ImgBox Button File (PDF/DOCX)
                                </label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="imgBox_buttonLink"
                                    name="imgBox_buttonLink"
                                    accept=".pdf,.docx"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        setImgBoxButtonFile(file || null);
                                    }}
                                />
                                {imgBoxButtonUrl && !imgBoxButtonFile && (
                                    <div className="mt-2 d-flex align-items-center gap-2">
                                        <a href={imgBoxButtonUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                            View existing PDF
                                        </a>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={handleRemoveImgBoxButtonFile}
                                        >
                                            <i className="fa fa-trash"></i>
                                        </button>
                                    </div>
                                )}

                                {imgBoxButtonFile && (
                                    <small className="text-success">Selected: {imgBoxButtonFile.name}</small>
                                )}
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_officeSpace" className="form-label">Office Space</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_officeSpace"
                                    name="imgBox_officeSpace"
                                    placeholder="Office Space"
                                    value={formData?.imgBox?.officeSpace}
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, officeSpace: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_retailSpace" className="form-label">Retail Space</label>
                                <input
                                    className="form-control mb-2"
                                    id="imgBox_retailSpace"
                                    name="imgBox_retailSpace"
                                    placeholdesr="Office Space"
                                    value={formData?.imgBox?.retailSpace}
                                    onChange={e => setFormData(p => ({
                                        ...p,
                                        imgBox: { ...p.imgBox, retailSpace: e.target.value }
                                    }))}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
                                <label htmlFor="imgBox_imgSrc" className="form-label">Image Box Image</label>
                                <input
                                    type="file"
                                    id="imgBox_imgSrc"
                                    name="imgBox_imgSrc"
                                    className="form-control"
                                    onChange={(e) =>
                                        handleSingleImageChange(e, setImgBoxImage, setImgBoxImagePreview)
                                    }
                                />
                                {imgBoxImagePreview && (
                                    <img
                                        src={imgBoxImagePreview}
                                        alt="ImgBox Preview"
                                        style={{ width: 100, height: 100, objectFit: "cover", marginTop: 10 }}
                                    />
                                )}
                            </div>

                            <h5>Project Gallery</h5>
                            {gallery.map((item, index) => (
                                <div key={index} className="row">
                                    <div className="mb-3 col-lg-4 col-md-4 col-sm-4">
                                        <input
                                            type="file"
                                            className="form-control"
                                            onChange={(e) =>
                                                handleGalleryImageChange(index, e.target.files[0])
                                            }
                                        />
                                        {item.preview && (
                                            <img
                                                src={item.preview}
                                                alt={`Gallery-${index}`}
                                                style={{
                                                    width: 100,
                                                    height: 100,
                                                    objectFit: "cover",
                                                    marginTop: 8
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                        <select
                                            className="form-select"
                                            value={item.type}
                                            onChange={(e) => {
                                                const updated = [...gallery];
                                                updated[index].type = e.target.value;
                                                setGallery(updated);
                                            }}
                                        >
                                            <option value="interior">Interior</option>
                                            <option value="exterior">Exterior</option>
                                        </select>
                                    </div>
                                    <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={item.include_dynamic_page}
                                                onChange={(e) => {
                                                    const updated = [...gallery];
                                                    updated[index].include_dynamic_page = e.target.checked;
                                                    setGallery(updated);
                                                }}
                                            />
                                            <label className="form-check-label">
                                                Include Dynamic Page
                                            </label>
                                        </div>
                                    </div>
                                    <div className="mb-3 col-lg-2 col-md-2 col-sm-2">
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={() => handleRemoveGalleryImage(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-primary mb-3"
                                onClick={async () => {
                                    if (gallery.length > 99) {
                                        const result = await Swal.fire({
                                            title: 'Notice',
                                            text: 'Maximum hundred gallery images are allowed',
                                            icon: 'warning'
                                        });

                                        if (!result.isConfirmed) return;
                                    } else {
                                        setGallery([
                                            ...gallery,
                                            {
                                                image: null,
                                                preview: null,
                                                type: "interior",
                                                include_dynamic_page: false,
                                            },
                                        ]);
                                    }
                                }}
                            >
                                + Add Image
                            </button>

                            {/* MOBILE GALLERY */}
                            <h5>Mobile Gallery</h5>
                            {mobileGallery.map((item, index) => (
                                <div key={index} className="row">
                                    <div className="mb-3 col-lg-4 col-md-4 col-sm-4">
                                        <input
                                            type="file"
                                            className="form-control"
                                            onChange={(e) =>
                                                handleMobileGalleryImageChange(index, e.target.files[0])
                                            }
                                        />
                                        {item.preview && (
                                            <img
                                                src={item.preview}
                                                alt={`Mobile-Gallery-${index}`}
                                                style={{
                                                    width: 100,
                                                    height: 100,
                                                    objectFit: "cover",
                                                    marginTop: 8
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                        <select
                                            className="form-select"
                                            value={item.type}
                                            onChange={(e) => {
                                                const updated = [...mobileGallery];
                                                updated[index].type = e.target.value;
                                                setMobileGallery(updated);
                                            }}
                                        >
                                            <option value="interior">Interior</option>
                                            <option value="exterior">Exterior</option>
                                        </select>
                                    </div>
                                    <div className="mb-3 col-lg-3 col-md-3 col-sm-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={item.include_dynamic_page}
                                                onChange={(e) => {
                                                    const updated = [...mobileGallery];
                                                    updated[index].include_dynamic_page = e.target.checked;
                                                    setMobileGallery(updated);
                                                }}
                                            />
                                            <label className="form-check-label">
                                                Include Dynamic Page
                                            </label>
                                        </div>
                                    </div>
                                    <div className="mb-3 col-lg-2 col-md-2 col-sm-2">
                                        <button
                                            type="button"
                                            className="btn btn-danger"
                                            onClick={() => handleRemoveMobileGalleryImage(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-primary mb-3"
                                onClick={async () => {
                                    if (mobileGallery.length > 99) {
                                        const result = await Swal.fire({
                                            title: 'Notice',
                                            text: 'Maximum hundred mobile gallery images are allowed',
                                            icon: 'warning'
                                        });

                                        if (!result.isConfirmed) return;
                                    } else {
                                        setMobileGallery([
                                            ...mobileGallery,
                                            {
                                                image: null,
                                                preview: null,
                                                type: "interior",
                                                include_dynamic_page: false,
                                            },
                                        ]);
                                    }
                                }}
                            >
                                + Add Mobile Image
                            </button>

                            <h5>Project Statistic</h5>
                            {projectStatistics.map((item, index) => (
                                <div key={index} className="row">
                                    <div className="mb-3 col-lg-5 col-md-5 col-sm-5">
                                        <label className="form-label">Key</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. Area"
                                            value={item.key}
                                            onChange={(e) =>
                                                handleStatisticChange(index, "key", e.target.value)
                                            }
                                        />
                                    </div>

                                    <div className="mb-3 col-lg-5 col-md-5 col-sm-5">
                                        <label className="form-label">Value</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. 3500 sqft"
                                            value={item.value}
                                            onChange={(e) =>
                                                handleStatisticChange(index, "value", e.target.value)
                                            }
                                        />
                                    </div>

                                    <div className="mb-3 col-lg-2 col-md-2 col-sm-2">
                                        <button
                                            type="button"
                                            className="mt-4 btn btn-danger"
                                            onClick={() => removeStatistic(index)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                className="btn btn-primary mb-3"
                                onClick={addStatistic}
                            >
                                + Add Statistic
                            </button>

                        </div>
                    </div>
                    <div className="card-footer">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="btn btn-sm btn-primary me-2"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <i className="spinner-border spinner-border-sm me-1"></i>
                                    Submit
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-floppy-disk me-1"></i>
                                    Submit
                                </>
                            )}
                        </button>
                        <Link to={ROUTES.PROJECTS} className="btn btn-sm btn-default">
                            <i className="fas fa-times-circle"></i> Cancel
                        </Link>
                    </div>
                </div>
            </form>
        </>
    )
}

export default EditProject